import React, { useEffect, useMemo, useState } from 'react';
import { audit } from '../../lib/api';
import { getIntlLocale } from '../../i18n/runtime';
import { ClipboardCheck, Loader2, AlertCircle } from 'lucide-react';

const GROUPS = {
  admin: 'Admin',
  draft: 'Drafts',
  post: 'Posts',
  inbox: 'Inbox',
  import: 'Imports',
  campaign: 'Campaigns',
  account: 'Accounts',
  webhook: 'Webhooks',
  job: 'Jobs',
};

function groupOf(action = '') {
  const root = action.split('.')[0];
  return GROUPS[root] || 'Other';
}

export default function Audit() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState('All');
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await audit.list();
        if (active) setItems(data.items || data.events || []);
      } catch (err) {
        if (active) {
          setError(err.message || 'Could not load audit events.');
          setItems([]);
        }
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const groups = useMemo(() => ['All', ...new Set((items || []).map((item) => groupOf(item.action)))], [items]);
  const filtered = (items || []).filter((item) => filter === 'All' || groupOf(item.action) === filter);

  return (
    <div className="app-page-shell bg-white" data-testid="audit-page">
      <header className="h-14 border-b border-[#E3E2E0] flex items-center justify-between px-6 sticky top-0 bg-white z-10">
        <div className="flex items-center gap-2">
          <ClipboardCheck className="w-5 h-5 text-[#6B6B6B]" />
          <h1 className="text-lg font-semibold text-[#191919]">Audit</h1>
        </div>
        {items && <span className="text-xs text-[#6B6B6B]">{items.length} events</span>}
      </header>

      <div className="app-page-scroll">
        <div className="max-w-6xl mx-auto p-6 space-y-4">
        {error && (
          <div className="flex items-start gap-2 rounded-lg border border-[#FF8080] bg-[#FFEBE9] px-3 py-2 text-sm text-[#CE3636]">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            {error}
          </div>
        )}

        {items !== null && items.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {groups.map((group) => (
              <button
                key={group}
                type="button"
                onClick={() => setFilter(group)}
                className={`h-8 px-3 text-xs rounded-lg border transition-colors ${
                  filter === group
                    ? 'bg-[#0A0A0A] text-white border-[#0A0A0A]'
                    : 'bg-white border-[#E3E2E0] text-[#6B6B6B] hover:text-[#191919]'
                }`}
                data-testid={`audit-filter-${group.toLowerCase()}`}
              >
                {group}
              </button>
            ))}
          </div>
        )}

        {items === null ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-[#6B6B6B]" />
          </div>
        ) : items.length === 0 ? (
          <div className="border border-dashed border-[#E3E2E0] rounded-xl py-16 px-6 text-center">
            <ClipboardCheck className="w-8 h-8 mx-auto text-[#9B9B9B]" />
            <h2 className="mt-3 text-base font-semibold text-[#191919]">No audit events yet</h2>
            <p className="mt-1 text-sm text-[#6B6B6B]">
              Impersonation, posts, approvals and worker actions will appear here.
            </p>
          </div>
        ) : (
          <div className="border border-[#E3E2E0] rounded-xl overflow-hidden bg-white">
            <table className="w-full text-sm">
              <thead className="bg-[#FBFBFA]">
                <tr>
                  <th className="text-left px-4 py-2 text-xs font-medium text-[#6B6B6B]">When</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-[#6B6B6B]">Action</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-[#6B6B6B]">Entity</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-[#6B6B6B]">Actor</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-[#6B6B6B]">Target</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E3E2E0]">
                {filtered.map((item) => (
                  <tr key={item.id} className="hover:bg-[#FBFBFA]">
                    <td className="px-4 py-2 text-xs text-[#6B6B6B] whitespace-nowrap">{formatWhen(item.created_at || item.ts)}</td>
                    <td className="px-4 py-2 font-mono text-xs text-[#191919]">{item.action}</td>
                    <td className="px-4 py-2 text-[#191919]">{item.entity_type || 'event'}</td>
                    <td className="px-4 py-2 text-xs text-[#6B6B6B]">{item.actor_email || item.actor || item.actor_type || 'system'}</td>
                    <td className="px-4 py-2 text-xs text-[#6B6B6B]">{item.target_email || item.entity_id || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}

function formatWhen(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString(getIntlLocale());
}
